import React, { useContext, useEffect, useState } from 'react';
import { formatISO } from 'date-fns';
import {
  Box, Card, Paper, Typography,
} from '@mui/material';
import { Bar, Line } from 'react-chartjs-2';
import { postData, randomRGB } from '../../../utils/utils';
import backgroundImage from '../../../images/FaintBackground.jpg';
import HamburgerIcon from '../../../components/HamburgerIcon/HamburgerIcon';
import { DataContext } from '../../../context/DataContext';
import { defaultChart } from './defaults';
import { barChartOptions, lineChartOptions } from './data';
import { DashboardDataContext } from '../../../context/DashboardDataContext';

type Props = {
  organisationIds: string[];
  onChartDataCheck?: (hasData: boolean) => void;
};

function Administration({ organisationIds = [], onChartDataCheck }: Props) {
  const [chartKey, setChartKey] = useState(0);
  const { setLoading } = useContext(DataContext);
  const [chartData, setChartData] = useState(defaultChart);
  const [totalEmissionTimeSeries, setTotalEmissionTimeSeries] = useState('');
  const [totalEmissionActivityChart, setTotalEmissionActivityChart] = useState('');
  const { calenderStartDate, calenderEndDate } = useContext(DashboardDataContext);

  const fetchChartData = async () => {
    if (!organisationIds.length) return;
    setLoading(true);
    try {
      const body = {
        organisation: organisationIds,
        report_types: ['emissions'],
        start_date: formatISO(calenderStartDate),
        end_date: formatISO(calenderEndDate),
        aggregate: true,
      };

      const response = await postData('/reports/instant-multi-report', body);

      if (response.status === 201) {
        setChartData(response.data);
        setChartKey((prev) => prev + 1); // Force re-render if needed

        const chartDataByActivity = response?.data?.chart_data?.chart_data_by_activity;
        const valuesArrayActivityChart = chartDataByActivity?.datasets[0]?.data || [];
        const valuesArrayTimeSeries = chartDataByActivity?.datasets[0]?.data || [];

        // Calculate total emission
        setTotalEmissionActivityChart(
          valuesArrayActivityChart
            .reduce((a: number, b: number) => a + b, 0)
            .toFixed(2),
        );
        setTotalEmissionTimeSeries(
          valuesArrayTimeSeries
            .reduce((a: number, b: number) => a + b, 0)
            .toFixed(2),
        );
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const processDataForLineChart = (feedData: any) => {
    if (
      !feedData?.chart_data?.chart_data_time_series
      || !feedData.chart_data.chart_data_time_series.labels
    ) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const limitData = (params: any, outputLength: number) => {
      const firstElement = params[0];
      let midElements = params.slice(1, -1);
      midElements = midElements.filter(
        (single: any, index: number) => index % Math.floor(params.length / outputLength) === 0,
      );
      const lastElement = params[params.length - 1];
      return [firstElement, ...midElements, lastElement];
    };

    const bottomLabels = feedData.chart_data.chart_data_time_series.labels;
    const dataToDisplayBeforeFormatting = feedData.chart_data.chart_data_time_series;
    const LIMIT_DATAPOINTS_HORIZONTALLY = 20;

    let labelToDisplayAfterFormatting = bottomLabels;
    if (bottomLabels.length > LIMIT_DATAPOINTS_HORIZONTALLY) {
      labelToDisplayAfterFormatting = limitData(
        bottomLabels,
        LIMIT_DATAPOINTS_HORIZONTALLY,
      );
    }

    const dataToDisplayAfterFormatting = dataToDisplayBeforeFormatting.datasets.map(
      (single: any, index: number) => {
        const { label, data } = single;
        let limitedDataPoints = data;

        if (bottomLabels.length > LIMIT_DATAPOINTS_HORIZONTALLY) {
          limitedDataPoints = limitData(data, LIMIT_DATAPOINTS_HORIZONTALLY);
        }

        let randomColor;
        if (index === 0) {
          // randomColor = randomRGB();
          randomColor = '97,185,195';
          return {
            label,
            data: limitedDataPoints,
            borderColor: `rgb(${randomColor})`,
            backgroundColor: `rgba(${randomColor},0.6)`,
            tension: 0,
            fill: true,
          };
        }
        // randomColor = randomRGB();
        randomColor = '200,200,200';
        return {
          label,
          data: limitedDataPoints,
          borderColor: `rgb(${randomColor})`,
          backgroundColor: `rgba(${randomColor},0.4)`,
          tension: 0,
          fill: true,
        };
      },
    );

    const data = {
      labels: labelToDisplayAfterFormatting,
      datasets: dataToDisplayAfterFormatting,
    };
    return data;
  };

  const processDataForBarChart = (feedData: any) => {
    if (
      !feedData?.chart_data?.chart_data_by_activity
      || !feedData.chart_data.chart_data_by_activity.labels
    ) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const { labels } = feedData.chart_data.chart_data_by_activity;
    const dataToDisplayBeforeFormatting = feedData.chart_data.chart_data_by_activity;
    const colorsValues = [
      '130,209,218',
      '54,190,179',
      '24,185,161',
      '26,169,162',
      '32,154,160',
      '34,127,155',
      '48,105,152',
      '65,84,149',
      '81,59,146',
      '84,44,108',
      '84,44,108',
      '190,243,243',
      '202,245,245',
      '213,247,247',
      '130,209,218',
      '54,190,179',
      '24,185,161',
      '26,169,162',
      '32,154,160',
      '34,127,155',
      '48,105,152',
    ];

    const dataToDisplayAfterFormatting = dataToDisplayBeforeFormatting.datasets.map(
      (single: any, index: number) => {
        let randomColor = randomRGB();
        if (index === 0) {
          const colors: string[] = [];
          for (
            let labelIndex = 0;
            labelIndex < labels.length;
            labelIndex += 1
          ) {
            if (labelIndex < colorsValues.length) {
              colors.push(`rgba(${colorsValues[labelIndex]},0.9)`);
            } else {
              colors.push(`rgba(${randomRGB()},0.9)`);
            }
          }
          return {
            ...single,
            borderColor: `rgb(${randomColor})`,
            backgroundColor: colors,
            tension: 0.4,
            fill: true,
          };
        }
        randomColor = '200,200,200';
        return {
          ...single,
          borderColor: `rgb(${randomColor})`,
          backgroundColor: `rgba(${randomColor},0.6)`,
          tension: 0.4,
          fill: true,
        };
      },
    );
    const data = { labels, datasets: dataToDisplayAfterFormatting };
    return data;
  };

  useEffect(() => {
    fetchChartData();
  }, [organisationIds, calenderStartDate, calenderEndDate]);

  const hasChartData = chartData?.chart_data?.chart_data_by_activity?.labels.length !== 0;

  useEffect(() => {
    if (onChartDataCheck) {
      onChartDataCheck(hasChartData);
    }
  }, [hasChartData]);

  return hasChartData ? (
    <Card
      sx={{
        margin: '20px',
        borderRadius: '20px',
        padding: '40px',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        top: '0px',
        right: '0px',
        bottom: '0px',
        left: '0px',
        opacity: 1,
        filter: 'brightness(1.05)',
        minHeight: 'calc(100% - 50px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HamburgerIcon />
      {/* Render diagrams */}
      <div className="thirdline">
        <Paper
          sx={{
            backgroundColor: '#fff',
            borderRadius: '20px',
            minHeight: '400px',
            padding: '20px 0',
            display: 'block',
          }}
        >
          <Box
            sx={{ padding: '5%', borderRadius: '20px', height: '400px' }}
          >
            {chartData?.chart_data?.chart_data_by_activity?.labels.length !== 0 ? (
              <Line
                key={`line-${chartKey}`}
                options={lineChartOptions}
                data={processDataForLineChart(chartData)}
              />
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="400px"
                width="100%"
                fontSize="xx-large"
                fontWeight="bold"
              >
                <Typography color="textSecondary">
                  No data available for selected organisation or filter.
                </Typography>
              </Box>
            )}
          </Box>
          {chartData?.chart_data?.chart_data_by_activity?.labels.length !== 0 ? (
            <Box sx={{ textAlign: 'center', color: 'rgb(100,100,100)' }}>
              <b>
                Total Emissions (tCO2-e):
                {' '}
                {totalEmissionTimeSeries}
              </b>
            </Box>
          ) : (
            ''
          )}
        </Paper>
        <Box sx={{ marginTop: '20px' }} />
        <Paper
          sx={{
            backgroundColor: '#fff',
            borderRadius: '20px',
            minHeight: '400px',
            padding: '20px 0',
            display: 'block',
          }}
        >
          <Box
            sx={{ padding: '5%', borderRadius: '20px', height: '400px' }}
          >
            {chartData?.chart_data?.chart_data_by_activity?.labels.length !== 0 ? (
              <Bar
                key={`bar-${chartKey}`}
                options={barChartOptions}
                data={processDataForBarChart(chartData)}
              />
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="400px"
                width="100%"
                fontSize="xx-large"
                fontWeight="bold"
              >
                <Typography color="textSecondary">
                  No data available for selected organisation or filter.
                </Typography>
              </Box>
            )}
          </Box>
          {chartData?.chart_data?.chart_data_by_activity?.labels.length !== 0 ? (
            <Box sx={{ textAlign: 'center', color: 'rgb(100,100,100)' }}>
              <b>
                Total Emissions (tCO2-e):
                {' '}
                {totalEmissionActivityChart}
              </b>
            </Box>
          ) : (
            ''
          )}
        </Paper>
      </div>
    </Card>
  ) : (
    <Box
      sx={{
        margin: '20px',
        padding: '40px',
        minHeight: 'calc(100% - 50px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#fff',
        borderRadius: '20px',
      }}
    >
      <Typography variant="h6" color="textSecondary">
        No data available for selected organisation or filter.
      </Typography>
    </Box>
  );
}

export default Administration;
